import { useMentionBoostCampaignConflictQuery } from '@/hooks/queries/use-mention-boost-campaign-conflict-query';
import { useMentionBoostCampaignsQuery } from '@/hooks/queries/use-mention-boost-campaigns-query';
import {
  MentionBoostCampaignBudgetMode,
  MentionBoostPlacementMode,
  type MentionBoostCampaignDto,
} from '@fluenceur/types';
import { useCallback, useEffect, useMemo, useState } from 'react';

// The search runs on the provider, so it waits for a pause in typing rather
// than firing a request per keystroke.
const SEARCH_DEBOUNCE_MS = 300;

export interface MentionBoostPlacementSelection {
  campaignId: string;
  campaignName: string;
}

interface UseMentionBoostPlacementOptions {
  mentionId: string;
  // The panel is open: nothing is fetched for a closed panel.
  isOpen: boolean;
  // The ad account the boost bills against; campaigns are listed on it.
  adAccountId: string | null;
  placementMode: MentionBoostPlacementMode;
  placementCampaignId: string | null;
  onPlacementChange: (
    mode: MentionBoostPlacementMode,
    campaign: MentionBoostPlacementSelection | null
  ) => void;
}

export interface MentionBoostPlacementState {
  isExpanded: boolean;
  toggleExpanded: () => void;
  placementMode: MentionBoostPlacementMode;
  selectMode: (mode: MentionBoostPlacementMode) => void;
  selectCampaign: (campaign: MentionBoostCampaignDto) => void;
  // Returns to the default placement, dropping any selection. Always reachable,
  // including when the campaign list cannot be read at all.
  resetToNewCampaign: () => void;
  placementCampaignId: string | null;
  // Compatible campaigns for the current search, as returned by the server.
  campaigns: MentionBoostCampaignDto[];
  isLoading: boolean;
  // The existing-campaign mode cannot be offered (campaigns unreadable).
  isUnavailable: boolean;
  hiddenCount: number;
  // The account holds more campaigns than the read ceiling: the list is partial
  // and the search is the way to reach the rest.
  isTruncated: boolean;
  search: string;
  setSearch: (value: string) => void;
  clearSearch: () => void;
  // True while a search term is applied, so an empty list reads as "no match"
  // instead of "no compatible campaign at all".
  hasSearch: boolean;
  // The selected campaign, once the listing resolved it.
  selectedCampaign: MentionBoostCampaignDto | null;
  // A campaign is stored but the unfiltered listing does not contain it
  // (deleted, archived, or on another ad account). Said out loud, never
  // silently degraded.
  isSelectionUnresolved: boolean;
  // The stored selection is not resolved yet: the budget section must not flash
  // the manual amount before the campaign turns out to be a CBO one.
  isResolvingSelection: boolean;
  // True when the selected campaign holds the budget itself (CBO): the daily
  // amount is then owned by the campaign, not by the boost.
  campaignBudgetManaged: boolean;
  // The ad set already boosting this post in the selected campaign, if any.
  conflictAdSetName: string | null;
}

/**
 * Owns everything the placement section needs that is NOT persisted with the
 * setup: the expand/collapse state, the campaign search, and the two read
 * queries. The persisted values (mode, campaign id, campaign name) stay in the
 * setup form, which is the single owner of the save payload.
 *
 * The campaigns are fetched when the section is opened OR when an existing
 * campaign is already selected — the latter is what resolves the budget mode
 * that decides whether the daily amount input is shown. A brand that never
 * opens the section pays no request at all.
 */
export function useMentionBoostPlacement({
  mentionId,
  isOpen,
  adAccountId,
  placementMode,
  placementCampaignId,
  onPlacementChange,
}: UseMentionBoostPlacementOptions): MentionBoostPlacementState {
  const [isExpanded, setIsExpanded] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const isExistingCampaign = placementMode === MentionBoostPlacementMode.EXISTING_CAMPAIGN;

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedSearch(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const { data, isLoading } = useMentionBoostCampaignsQuery({
    mentionId,
    adAccountId,
    search: debouncedSearch.length > 0 ? debouncedSearch : null,
    enabled: isOpen && (isExpanded || isExistingCampaign),
  });

  const campaigns = useMemo(() => data?.campaigns ?? [], [data?.campaigns]);

  const selectedCampaign = useMemo(
    () => campaigns.find((campaign) => campaign.id === placementCampaignId) ?? null,
    [campaigns, placementCampaignId]
  );

  const { data: conflictData } = useMentionBoostCampaignConflictQuery({
    mentionId,
    campaignId: isExistingCampaign ? placementCampaignId : null,
    enabled: isOpen && isExistingCampaign,
  });

  const resetToNewCampaign = useCallback(() => {
    onPlacementChange(MentionBoostPlacementMode.NEW_CAMPAIGN, null);
  }, [onPlacementChange]);

  const selectMode = useCallback(
    (mode: MentionBoostPlacementMode) => {
      // Switching back to a new campaign always drops the selection, so a stale
      // campaign can never travel with the next launch.
      onPlacementChange(mode, null);
    },
    [onPlacementChange]
  );

  const selectCampaign = useCallback(
    (campaign: MentionBoostCampaignDto) => {
      onPlacementChange(MentionBoostPlacementMode.EXISTING_CAMPAIGN, {
        campaignId: campaign.id,
        campaignName: campaign.name,
      });
    },
    [onPlacementChange]
  );

  const hasLoadedCampaigns = data !== undefined;

  return {
    isExpanded,
    toggleExpanded: useCallback(() => setIsExpanded((current) => !current), []),
    placementMode,
    selectMode,
    selectCampaign,
    resetToNewCampaign,
    placementCampaignId,
    campaigns,
    isLoading,
    isUnavailable: data?.unavailable === true,
    hiddenCount: data?.hiddenCount ?? 0,
    isTruncated: data?.truncated === true,
    search,
    setSearch,
    clearSearch: useCallback(() => setSearch(''), []),
    hasSearch: debouncedSearch.length > 0,
    selectedCampaign,
    // Only claim the selection is gone once an UNFILTERED listing came back: a
    // searched list legitimately excludes the selected campaign.
    isSelectionUnresolved:
      isExistingCampaign &&
      !!placementCampaignId &&
      hasLoadedCampaigns &&
      data?.unavailable !== true &&
      debouncedSearch.length === 0 &&
      !selectedCampaign,
    isResolvingSelection: isExistingCampaign && !!placementCampaignId && !hasLoadedCampaigns,
    campaignBudgetManaged:
      isExistingCampaign &&
      selectedCampaign?.budgetMode === MentionBoostCampaignBudgetMode.CAMPAIGN,
    conflictAdSetName: conflictData?.conflict?.adSetName ?? null,
  };
}
